import { useEffect } from "react";
import { Spin, Typography } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { useEmailDocumentQuery } from "../../../api/graphql/queries/emailDocuments";
import { formatAmountInCents } from "../../../utils";
import { DISPLAY_DATE_FORMAT } from "../../../constants";
import TransactionListContainer from "../BelegeView/TransactionList/TransactionListContainer";
import NoMatchSection from "../BelegeView/NoMatchSection";

/**
 * "Matched transaction" section shown in the receipt detail panel.
 *
 * - Matched receipt (SMT-1035): a read-only card with the matched transaction's
 *   details (name / memo / amount / date), linking out to the transaction. Falls
 *   back to a plain link when `matchedTransaction` isn't available yet (e.g. the
 *   SMT-1029 backend field not deployed) so the panel degrades gracefully.
 * - Unmatched receipt (SMT-980): the existing BelegeView matching UI (search +
 *   suggested transactions + "create external transaction").
 *
 * Both states are fed by the per-document `emailDocument` query (it carries
 * `transactionMatches` + `iban` + `matchedTransaction` that the list page omits).
 */
export const ReceiptMatchedTransactionSection = ({
  email,
  documentId,
  matchedTransactionId,
  onChanged,
}: {
  email?: string;
  documentId: string;
  matchedTransactionId?: string | null;
  onChanged: () => void | Promise<unknown>;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  const [loadDocument, { data, loading, error }] = useEmailDocumentQuery();

  // Fetch the full document whenever we have the customer email: unmatched
  // receipts need transactionMatches + OCR fields, matched receipts need the
  // matchedTransaction details for the card. The queries are keyed by email.
  useEffect(() => {
    if (email) {
      loadDocument({ variables: { email, options: { id: documentId } } });
    }
  }, [email, documentId, loadDocument]);

  const transactionLink = () => (
    <span>
      {t("match.alreadyMatched")}{" "}
      <a
        data-test="matchedTransactionLink"
        href={`/transaction-view?id=${matchedTransactionId}`}
        target="_blank"
        rel="noreferrer"
      >
        {t("match.viewTransaction")}
      </a>
    </span>
  );

  const renderBody = () => {
    if (matchedTransactionId) {
      // Spin while the per-document fetch is in flight (only when we fired it).
      if (email && (loading || (!data && !error))) {
        return <Spin />;
      }

      const matched = data?.emailDocument?.matchedTransaction;
      if (!matched) {
        return transactionLink();
      }

      return (
        <a
          data-test="matchedTransactionCard"
          href={`/transaction-view?id=${matchedTransactionId}`}
          target="_blank"
          rel="noreferrer"
          aria-label={t("match.viewTransaction") ?? undefined}
        >
          <div className="flex justify-between rounded border p-3">
            <div>
              <div>{matched.name ?? t("match.unknownTransaction")}</div>
              {matched.description && (
                <Typography.Text type="secondary">
                  {matched.description}
                </Typography.Text>
              )}
            </div>
            <div className="text-right">
              <div>
                {formatAmountInCents(
                  matched.amount,
                  true,
                  undefined,
                  matched.currency
                )}
              </div>
              {matched.valutaDate && (
                <Typography.Text type="secondary">
                  {moment(matched.valutaDate).format(DISPLAY_DATE_FORMAT)}
                </Typography.Text>
              )}
            </div>
          </div>
        </a>
      );
    }

    // Can't match without the customer email — render nothing rather than firing
    // a query with an invalid identifier.
    if (!email) {
      return null;
    }

    // Spin while the fetch is pending (or before it has started). Once it
    // resolves, surface a failure/empty result instead of spinning forever.
    if (loading || (!data && !error)) {
      return <Spin />;
    }

    const document = data?.emailDocument;
    if (error || !document) {
      return (
        <Typography.Text type="secondary">
          {t("match.loadError")}
        </Typography.Text>
      );
    }

    return (
      <>
        <TransactionListContainer
          transactions={document.transactionMatches}
          email={email}
          currentEmailDocument={document}
          onMatchSuccess={onChanged}
        />
        <NoMatchSection
          email={email}
          emailDocument={document}
          onCreateExternalTransaction={onChanged}
        />
      </>
    );
  };

  return (
    <div>
      <Typography.Title level={5}>{t("match.sectionTitle")}</Typography.Title>
      {renderBody()}
    </div>
  );
};
